/**
 * Quoted lines.
 *
 * An estimator often has a number before they have an archetype: a railing
 * post quoted by a vendor, a specialty part nobody has a rate card for. The
 * screen used to demand a product type anyway, so those lines were shoehorned
 * into whatever type looked closest (railing posts entered as Columns), and
 * the trace then claimed a rate card and a geometry the line never had.
 *
 * A line that carries a quoted price with an amount above zero and no product
 * type and no catalog product is a QUOTED LINE: the backend prices it from the
 * quote, plus any hours typed in by hand, and reports
 * `standalone_quoted_line: true` when it took that path. It is a complete,
 * priceable line — not an unfinished product line — and every surface that
 * says "pick a product type" has to know the difference.
 */

/** The amount field, present and above zero. Blank/0 is not a quoted price. */
export function quotedPriceAmount(lineItem) {
  const raw = lineItem?.material_price_override;
  if (raw === null || raw === undefined || raw === "") return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/** True when this line carries a usable quoted price. */
export function hasQuotedPrice(lineItem) {
  return quotedPriceAmount(lineItem) !== null;
}

/**
 * True for a Product-mode line that prices from its quoted price alone.
 * Custom lines are excluded: they already price from their components.
 */
export function isStandaloneQuotedLine(lineItem) {
  if (!lineItem || lineItem.mode === "custom") return false;
  if (lineItem.product_type_key || lineItem.product_id) return false;
  return hasQuotedPrice(lineItem);
}

/**
 * True when the backend confirmed it priced this line from the quote alone.
 * Falls back to the form's own view so the label is right before the first
 * calculation returns.
 */
export function pricedAsStandaloneQuotedLine(result, lineItem) {
  return result?.standalone_quoted_line === true
    ? true
    : isStandaloneQuotedLine(lineItem);
}
